import {
  getKycConsiderResult,
  getKycDocumentStatus,
  KYC_TOOLTIP_PRIORITY,
} from "./common";
import { getKycScoreState } from "./profileCompletionScore";

// Company login tenants (KYB) only. Reasons shown next to the KYB and
// authorised signatory statuses. The person reason follows the same checks,
// in the same order, as the tenant KYC reason in TenantHeaderCard; the tenant
// flow does not use this file.

const getPersonName = (person) =>
  person?.kyc_data?.full_name ||
  [person?.kyc_data?.first_name, person?.kyc_data?.last_name]
    .filter(Boolean)
    .join(" ") ||
  person?.name ||
  "-";

const getPersonReason = (person, _t, llShowOnly) => {
  const kycChecks = person?.kyc_checks;
  if (
    getKycDocumentStatus({ onfido_user_kyc: person }) === "review" &&
    person?.kyc_data?.document_type === "unidentified"
  ) {
    return llShowOnly
      ? _t("onfidoUnidentifiedDocumentReviewLandlordMessage")
      : _t("onfidoUnidentifiedDocumentReviewTenantMessage");
  }
  if (!kycChecks) return _t("onfidoFallbackMessage");
  if (
    getKycConsiderResult(
      kycChecks,
      "image_integrity.supported_document.properties.issuing_country_subject_to_sanctions"
    )
  ) {
    return _t("onfidoImageIntegritySanctionsMessage");
  }
  for (const { path, message, explanation } of KYC_TOOLTIP_PRIORITY) {
    if (getKycConsiderResult(kycChecks, path)) {
      return path.includes("image_quality")
        ? `${_t(message)}: ${_t(explanation)}`
        : _t(message);
    }
  }
  // Review or declined with clear document checks (for example AML).
  return _t("onfidoFallbackMessage");
};

// One "Name: reason" line per signatory in review or declined.
export const getKybPeopleReasons = (user, _t, llShowOnly) =>
  (user?.onfido_user_kyc?.kyb_people || [])
    .filter((person) =>
      ["review", "declined"].includes(
        getKycScoreState({ onfido_user_kyc: person })
      )
    )
    .map(
      (person) =>
        `${getPersonName(person)}: ${getPersonReason(person, _t, llShowOnly)}`
    );

// KYB status reasons: the signatories' reasons, or a company message when
// the review / decline is not caused by a signatory.
export const getKybReasons = (user, _t, llShowOnly) => {
  const state = getKycScoreState(user);
  if (!["review", "declined"].includes(state)) return [];
  const peopleReasons = getKybPeopleReasons(user, _t, llShowOnly);
  if (peopleReasons.length) return peopleReasons;
  return [
    state === "declined"
      ? _t(
          "kybCompanyDeclinedReason",
          "Company KYB status tooltip: reason when the company check is declined and no signatory caused it"
        )
      : _t(
          "kybCompanyReviewReason",
          "Company KYB status tooltip: reason when the company check is in review and no signatory caused it"
        ),
  ];
};

// Registry officer: verified when Didit's own KYC for them is approved, or a
// submitted signatory with the same name (case and spaces ignored, as Didit
// matches people) is approved.
const normaliseName = (name) =>
  (name || "").trim().replace(/\s+/g, " ").toLowerCase();

export const isRegistryOfficerVerified = (officer, user) => {
  if (officer?.kyc_status === "Approved") return true;
  const name = normaliseName(officer?.name);
  if (!name) return false;
  return (user?.onfido_user_kyc?.kyb_people || []).some(
    (person) =>
      normaliseName(person?.name) === name &&
      getKycScoreState({ onfido_user_kyc: person }) === "approved"
  );
};
